"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  Plus,
  Edit3,
  Trash2,
  ChevronDown,
  ChevronUp,
  HelpCircle,
} from "lucide-react";
import FAQModal from "./FAQModal";
import { useFAQs } from "../context/FAQContext";

const FAQPage = () => {
  const {
    faqs: contextFaqs = [],
    loading,
    error,
    fetchFaqs,
    createFaq,
    updateFaq,
    deleteFaq,
  } = useFAQs();

  useEffect(() => {
    if (typeof fetchFaqs === "function") {
      fetchFaqs().catch(() => {});
    }
  }, [fetchFaqs]);

  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);

  const faqs = contextFaqs;

  const statusOptions = [
    { value: "all", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const filteredFAQs = useMemo(() => {
    return faqs.filter((faq) =>
      statusFilter === "all" ? true : faq.status === statusFilter
    );
  }, [faqs, statusFilter]);

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "high":   return "text-red-600 bg-red-100";
      case "medium": return "text-yellow-600 bg-yellow-100";
      case "low":    return "text-green-600 bg-green-100";
      default:       return "text-gray-600 bg-gray-100";
    }
  };

  const handleDeleteFAQ = async (faqId) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    try {
      if (typeof deleteFaq === "function") await deleteFaq(faqId);
    } catch (err) {
      alert(err.message || "Failed to delete FAQ");
    }
  };

  const handleEditFAQ = (faq) => {
    setEditingFAQ(faq);
    setShowAddModal(true);
  };

  const handleToggleStatus = async (faqId, currentStatus) => {
    try {
      if (!updateFaq) return;
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      const updateAuditId = localStorage.getItem("admin_id") || 1;
      await updateFaq(faqId, { status: newStatus, update_audit_id: updateAuditId });
    } catch (err) {
      alert(err.message || "Failed to toggle status");
    }
  };

  const handleSaveFAQ = async (faqData) => {
    try {
      if (editingFAQ || faqData.id) {
        const id = faqData.id || editingFAQ.id || editingFAQ._id;
        if (typeof updateFaq === "function") await updateFaq(id, faqData);
      } else {
        if (typeof createFaq === "function") await createFaq(faqData);
      }
      setEditingFAQ(null);
      setShowAddModal(false);
    } catch (err) {
      alert(err.message || "Failed to save FAQ");
    }
  };

  // ─── TABLE VIEW ───────────────────────────────────────────────────────────────
  const FAQTableView = () => {
    const [expandedRow, setExpandedRow] = useState(null);

    return (
      <div className="bg-white bg-opacity-80 rounded-xl border border-orange-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-teal-50 border-b border-orange-200">
                <th className="px-4 py-3 text-left font-semibold text-teal-800">ID</th>
                <th className="px-4 py-3 text-left font-semibold text-teal-800">Question</th>
                <th className="px-4 py-3 text-left font-semibold text-teal-800">Category</th>
                <th className="px-4 py-3 text-left font-semibold text-teal-800">Priority</th>
                <th className="px-4 py-3 text-left font-semibold text-teal-800">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-teal-800">Created</th>
                <th className="px-4 py-3 text-center font-semibold text-teal-800">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFAQs.map((faq, index) => (
                <React.Fragment key={faq.id}>
                  <tr className="border-b border-gray-100 hover:bg-orange-50/40 transition-colors">
                    <td className="px-4 py-3 text-gray-500 font-medium">{index + 1}</td>

                    {/* Question – click to expand answer */}
                    <td className="px-4 py-3 max-w-xs">
                      <button
                        onClick={() => setExpandedRow(expandedRow === faq.id ? null : faq.id)}
                        className="text-left group focus:outline-none"
                      >
                        <span className="font-medium text-teal-800 group-hover:text-teal-600 transition-colors line-clamp-2">
                          {faq.question}
                        </span>
                        <span className="ml-1 inline-block text-gray-400">
                          {expandedRow === faq.id
                            ? <ChevronUp className="w-3.5 h-3.5 inline" />
                            : <ChevronDown className="w-3.5 h-3.5 inline" />}
                        </span>
                      </button>
                    </td>

                    <td className="px-4 py-3">
                      {faq.category
                        ? <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium whitespace-nowrap">{faq.category}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>

                    <td className="px-4 py-3">
                      {faq.priority
                        ? <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${getPriorityBadge(faq.priority)}`}>{faq.priority}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>

                    {/* Status Toggle */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(faq.id, faq.status)}
                        title={faq.status === "Active" ? "Click to Deactivate" : "Click to Activate"}
                        className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-500 ${
                          faq.status === "Active" ? "bg-green-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${
                            faq.status === "Active" ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>

                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {faq.createdAt ? new Date(faq.createdAt).toLocaleDateString() : "—"}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEditFAQ(faq)}
                          className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFAQ(faq.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expandable answer row */}
                  {expandedRow === faq.id && (
                    <tr className="bg-teal-50/30">
                      <td colSpan={7} className="px-6 py-4 border-b border-gray-100">
                        <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide block mb-1">
                          Answer
                        </span>
                        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                          {faq.answer}
                        </p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-right">
          Showing {filteredFAQs.length} of {faqs.length} FAQs
        </div>
      </div>
    );
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">

        {/* Page Header */}
        <header className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-teal-800">Frequently Asked Questions</h1>
              <p className="text-gray-600 mt-1">Manage customer support FAQs</p>
            </div>
          </div>

          <button
            onClick={() => { setEditingFAQ(null); setShowAddModal(true); }}
            className="flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Add FAQ
          </button>
        </header>

        {/* Status Filter */}
        <div className="flex items-center gap-2 mb-6">
          {statusOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                statusFilter === opt.value
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white text-gray-600 border-orange-200 hover:bg-orange-50"
              }`}
            >
              {opt.label}
              {opt.value !== "all" && (
                <span className="ml-1.5 text-xs opacity-70">
                  ({faqs.filter((f) => f.status === opt.value).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 p-4 rounded mb-6 text-red-700">
            {error}
          </div>
        )}

        {/* Table or Empty State */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading...</div>
        ) : filteredFAQs.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <HelpCircle className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">No FAQs found</h2>
            <p className="text-gray-600 mb-6">
              {statusFilter !== "all"
                ? "Try changing the status filter."
                : "Create your first FAQ to help customers find answers quickly."}
            </p>
            <button
              onClick={() => { setEditingFAQ(null); setShowAddModal(true); }}
              className="bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium"
            >
              Add FAQ
            </button>
          </div>
        ) : (
          <FAQTableView />
        )}
      </div>

      <FAQModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingFAQ(null); }}
        faq={editingFAQ}
        categories={[...new Set(faqs.map((f) => f.category))]}
        onSave={handleSaveFAQ}
      />

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default FAQPage;